import { useTraduccion } from '@tallerya/i18n';
import { useEffect, useId, useRef, useState, type CSSProperties, type MouseEvent } from 'react';
import { IconoInfo } from './iconos';
import './ui.css';

/** El ancho máximo del globo y lo que se reserva para decidir si cabe abajo del ⓘ. */
const ANCHO_MAXIMO = 320;
const ALTO_ESTIMADO = 160;
const MARGEN = 8;

/**
 * El ⓘ junto a un título o una etiqueta, con el globo que explica qué es (#120).
 * Se abre con un toque y no al pasar el mouse, porque en el celular no hay *hover*.
 *
 * El globo va fijo, como el de `Menu`: absoluto se salía de la pantalla del
 * celular y quedaba recortado dentro de una `Ventana`. Por ser fijo se cierra al
 * desplazar, o se quedaría flotando lejos de su ⓘ.
 *
 *   <Ayuda tema="Código" texto="Se asigna solo. Puedes reemplazarlo." />
 *
 * `tono="titulo"` es el de un título de pantalla o de sección: relleno negro con
 * la «i» en blanco, como en el Figma. El de una etiqueta va en contorno gris. Así
 * el ⓘ que explica la pantalla no se confunde con el de un campo.
 */
export function Ayuda({ tema, texto, tono = 'campo' }: { tema: string; texto: string; tono?: 'titulo' | 'campo' }) {
  const { t } = useTraduccion();
  const [posicion, setPosicion] = useState<CSSProperties | null>(null);
  const abierto = posicion !== null;
  const raiz = useRef<HTMLSpanElement>(null);
  const boton = useRef<HTMLButtonElement>(null);
  const id = useId();

  function alternar(evento: MouseEvent<HTMLButtonElement>) {
    if (abierto) {
      setPosicion(null);
      return;
    }
    const caja = evento.currentTarget.getBoundingClientRect();
    const ancho = Math.min(ANCHO_MAXIMO, window.innerWidth - 2 * MARGEN);
    const left = Math.max(MARGEN, Math.min(caja.left, window.innerWidth - MARGEN - ancho));
    setPosicion(
      window.innerHeight - caja.bottom >= ALTO_ESTIMADO
        ? { top: caja.bottom + 4, left, maxWidth: ancho }
        : { bottom: window.innerHeight - caja.top + 4, left, maxWidth: ancho },
    );
  }

  useEffect(() => {
    if (!abierto) return;

    const cerrar = () => setPosicion(null);
    function alTocar(evento: PointerEvent) {
      if (!raiz.current?.contains(evento.target as Node)) cerrar();
    }
    function alTeclear(evento: KeyboardEvent) {
      if (evento.key !== 'Escape') return;
      // Este Escape es del globo: sin esto, el `<dialog>` que lo contiene —el
      // `PanelLateral`— también lo recibe y se cierra entero.
      evento.preventDefault();
      cerrar();
      boton.current?.focus();
    }

    document.addEventListener('pointerdown', alTocar);
    document.addEventListener('keydown', alTeclear);
    window.addEventListener('scroll', cerrar, { capture: true });
    window.addEventListener('resize', cerrar);
    return () => {
      document.removeEventListener('pointerdown', alTocar);
      document.removeEventListener('keydown', alTeclear);
      window.removeEventListener('scroll', cerrar, { capture: true });
      window.removeEventListener('resize', cerrar);
    };
  }, [abierto]);

  return (
    <span className={`ui-ayuda ui-ayuda--${tono}`} ref={raiz}>
      <button
        ref={boton}
        type="button"
        className="ui-ayuda__boton"
        aria-label={t('ui.ayuda.abrir', { tema })}
        aria-expanded={abierto}
        aria-controls={id}
        onClick={alternar}
      >
        <IconoInfo relleno={tono === 'titulo'} />
      </button>
      {/* La región existe siempre para que el lector anuncie el texto cuando aparece. */}
      <span id={id} aria-live="polite">
        {posicion && (
          <span className="ui-ayuda__globo" style={posicion}>
            {texto}
          </span>
        )}
      </span>
    </span>
  );
}
